import { Injectable } from "@angular/core";
import { Constants } from "@app/constants/constants";
import { USER_MENU_ROUTES, ADMIN_MENU_ROUTES } from "@app/constants/menu-routes.const";
import { AuthenticationService } from "@app/services/authentication.service";
import { NgxPermissionsService } from "ngx-permissions";

@Injectable({
    providedIn: 'root'
})
export class AuthorizationService {
    private permissionItems!: Array<string>;
    private userRolesList: any[] = [];

    constructor(private permissionsService: NgxPermissionsService,
                private authenticationService: AuthenticationService) {
    }

    public loadPermissions(permissions: string[]) {
        this.permissionsService.loadPermissions(permissions);
    }

    public flushPermissions() {
        this.permissionsService.flushPermissions();
        this.userRolesList = [];
        this.permissionItems = [];
    }

    private permissions = {
        [Constants.ACL.Admin]: [Constants.UserRole.ADMIN],
        [Constants.ACL.NormalUser]: [Constants.UserRole.ADMIN, Constants.UserRole.USER]
    };

    public hasPermission(permissionSlug: string) {
        this.getCurrentUserInfo(); 
        if (this.permissionItems && permissionSlug) {
            if (this.permissionItems.indexOf(permissionSlug.toLowerCase()) > -1) {
                return true;
            }
        }
        return false;
    }

    public hasPermissionSection(sectionName: string): boolean {
        this.loadUserRolesList();
        let hasAccess = false;
        this.userRolesList.forEach(role => {
            role.permissionSections?.forEach(section => {
                if (section.enabled && section.sectionName.toLowerCase() === sectionName.toLowerCase()) {
                    hasAccess = section.enabled;
                }
            });
        });
        return hasAccess;
    }

    public hasAnyPermission(permissionSlugs: string[]): boolean {
        return permissionSlugs.some(slug => this.hasPermission(slug));
    }

    private loadUserRolesList(): void {
        const roles = this.authenticationService.getUserRoles();
        this.userRolesList = roles ? JSON.parse(roles) : [];
    }

    public isAdmin() {
        let loginInfo = this.getCurrentUserInfo();
        if (loginInfo?.roles) {
            return loginInfo.roles.some(role => role === "ADMIN" || role === "ADMINISTRATOR" || role === "SUPERADMINISTRATOR");
        }
        return false;
    }

    public isSuperAdmin() {
        let loginInfo = this.getCurrentUserInfo();
        if (loginInfo?.roles) {
            return loginInfo.roles.some(role => role === Constants.UserRole.SUPERADMIN);
        }
        return false;
    }

    public isAdministrator() {
        let loginInfo = this.getCurrentUserInfo();
        if (loginInfo?.roles) {
            return loginInfo.roles.some(role => role === Constants.UserRole.ADMIN);
        }
        return false;
    }

    private getCurrentUserInfo(): any {
        const currentUser = this.authenticationService.getCurrentUserInfo();
        if (currentUser) {
            if (currentUser.permissions) {
                this.permissionItems = [...currentUser.permissions].map(element => element.toLowerCase());
            }
            return currentUser;
        }
        return null;
    }

    public getFirstAccessibleAdminRoute(): string {
        const match = ADMIN_MENU_ROUTES.find(route => route.permissionCheck(this));
        return match ? match.path : '/forbidden';
    }

    public getFirstAccessibleUserRoute(): string {
        if (this.isAdmin()) {
            return this.getFirstAccessibleAdminRoute();
        }

        const defaultRoute = '/user/customers';
        if (this.hasPermissionSection('customers')) {
            return defaultRoute;
        }

        const match = USER_MENU_ROUTES.find(route => route.permissionCheck(this));
        return match ? match.path : '/forbidden';
    }
}
